// src/repositories/supabase/SupabaseSunatRecordRepository.ts
import type { SupabaseClient } from '@supabase/supabase-js';
import type { ISunatRecordRepository, DBSunatRecord } from '../types';
import { requireAuth } from '../../services/supabaseClient';

function mapRowToDBSunatRecord(row: Record<string, unknown>): DBSunatRecord {
  return {
    dbId:            (row.db_id as number | null) ?? undefined,
    firmId:          row.firm_id as string,
    companyId:       row.company_id as string,
    companyRuc:      row.company_ruc as string,
    tipo:            row.tipo as 'compra' | 'venta',
    periodo:         row.periodo as string,
    contenidoRaw:    row.contenido_raw as string,
    registrosParsed: row.registros_parsed as number,
    importedAt:      row.imported_at as string,
    createdBy:       (row.created_by as string | null) ?? undefined,
  };
}

export class SupabaseSunatRecordRepository implements ISunatRecordRepository {
  private readonly client: SupabaseClient;
  constructor(client: SupabaseClient) { this.client = client; }

  async getByType(
    firmId: string,
    companyId: string,
    tipo: 'compra' | 'venta',
    periodo?: string,
  ): Promise<DBSunatRecord[]> {
    await requireAuth();
    // Normalize "YYYY-MM" → "YYYYMM" to match the stored format
    const normalizedPeriodo = periodo ? periodo.replace('-', '') : undefined;

    let query = this.client
      .from('sunat_records')
      .select('db_id, firm_id, company_id, company_ruc, tipo, periodo, contenido_raw, registros_parsed, imported_at, created_by')
      .eq('firm_id', firmId)
      .eq('company_id', companyId)
      .eq('tipo', tipo)
      .limit(2000);

    if (normalizedPeriodo) {
      query = query.eq('periodo', normalizedPeriodo) as typeof query;
    }

    const { data, error } = await query;
    if (error) throw new Error(`[getByType] ${error.message}`);
    return (data ?? []).map(mapRowToDBSunatRecord);
  }

  async getLatest(
    firmId: string,
    companyId: string,
    tipo: 'compra' | 'venta',
  ): Promise<DBSunatRecord | undefined> {
    await requireAuth();
    const { data, error } = await this.client
      .from('sunat_records')
      .select('db_id, firm_id, company_id, company_ruc, tipo, periodo, contenido_raw, registros_parsed, imported_at, created_by')
      .eq('firm_id', firmId)
      .eq('company_id', companyId)
      .eq('tipo', tipo)
      .order('imported_at', { ascending: false })
      .limit(1);
    if (error) throw new Error(`[getLatest] ${error.message}`);
    const rows = data ?? [];
    return rows.length > 0 ? mapRowToDBSunatRecord(rows[0]) : undefined;
  }
}
